import { api, endpoints } from '../api/config';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import DashboardLayout from '../components/DashboardLayout';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { PDFDocument } from 'pdf-lib';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
  Checkbox,
  Alert,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { getFutureDateInputProps } from '../utils/dateConstraints';
import {
  COLUMN_MIN_SIZE,
  COLUMN_SIZE,
  DIALOG_CLOSE_BUTTON_STYLE,
  NEAR_FULLSCREEN_DIALOG_PAPER_SX,
} from '../constants/styles';

const LTL_FILTER_STORAGE_KEY = 'LTL Sales Orders-filter-items';
const SESSION_FILTER_KEY = 'ltl-applied-filters-session';
const FILTER_TTL_MS = 10 * 60 * 1000; // 10 minutes in milliseconds

const getInitialFilterState = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_FILTER_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && typeof parsed.timestamp === 'number') {
        if (Date.now() - parsed.timestamp <= FILTER_TTL_MS) {
          return {
            shipDateFrom: parsed.shipDateFrom || '',
            shipDateTo: parsed.shipDateTo || '',
            transactionId: parsed.transactionId || '',
            poNumber: parsed.poNumber || '',
          };
        }
      }
    }
  } catch {
    // ignore parse error
  }
  // Expiration (>10 minutes) or no saved filters: clear sessionStorage & localStorage
  sessionStorage.removeItem(SESSION_FILTER_KEY);
  localStorage.removeItem('ltl-ship-date-from');
  localStorage.removeItem('ltl-ship-date-to');
  localStorage.removeItem('ltl-transaction-id');
  localStorage.removeItem('ltl-po-number');
  return {
    shipDateFrom: '',
    shipDateTo: '',
    transactionId: '',
    poNumber: '',
  };
};

const SalesOrders = () => {
  const [initialFilters] = useState(getInitialFilterState);
  const [shipDateFrom, setShipDateFrom] = useState(initialFilters.shipDateFrom);
  const [shipDateTo, setShipDateTo] = useState(initialFilters.shipDateTo);
  const [transactionId, setTransactionId] = useState(initialFilters.transactionId);
  const [poNumber, setPoNumber] = useState(initialFilters.poNumber);
  const [shipDateFromDraft, setShipDateFromDraft] = useState(initialFilters.shipDateFrom);
  const [shipDateToDraft, setShipDateToDraft] = useState(initialFilters.shipDateTo);
  const [transactionIdDraft, setTransactionIdDraft] = useState(initialFilters.transactionId);
  const [poNumberDraft, setPoNumberDraft] = useState(initialFilters.poNumber);

  const [lineLevelDialogOpen, setLineLevelDialogOpen] = useState(false);
  const [lineLevelAllowNested, setLineLevelAllowNested] = useState(true);
  const [lineLevelLoading, setLineLevelLoading] = useState(false);
  const [lineLevelError, setLineLevelError] = useState(null);
  const [lineLevelData, setLineLevelData] = useState([]);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);

  const [detailedDialogOpen, setDetailedDialogOpen] = useState(false);
  const [detailedLoading, setDetailedLoading] = useState(false);
  const [detailedData, setDetailedData] = useState([]);

  const [selectedLineLevelRows, setSelectedLineLevelRows] = useState(new Set());
  const [selectedDetailedRows, setSelectedDetailedRows] = useState(new Set());

  const [labelImageOpen, setLabelImageOpen] = useState(false);
  const [labelImages, setLabelImages] = useState([]);
  const [allZplLabels, setAllZplLabels] = useState([]);
  const [labelLoading, setLabelLoading] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [labelError, setLabelError] = useState(null);

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('ltl-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });
  const [lineLevelColumnVisibility, setLineLevelColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('ltl-linelevel-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });
  const [detailedColumnVisibility, setDetailedColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('ltl-detailed-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    localStorage.setItem('ltl-column-visibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  useEffect(() => {
    localStorage.setItem('ltl-linelevel-column-visibility', JSON.stringify(lineLevelColumnVisibility));
  }, [lineLevelColumnVisibility]);

  useEffect(() => {
    localStorage.setItem('ltl-detailed-column-visibility', JSON.stringify(detailedColumnVisibility));
  }, [detailedColumnVisibility]);

  // ── Persistent DataGridPro column filters ────────────────────────────────
  const [savedFilterItems, setSavedFilterItems] = useState(() => {
    try {
      const raw = localStorage.getItem(LTL_FILTER_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const handleColumnFiltersChange = useCallback((items) => {
    setSavedFilterItems(items);
    try {
      localStorage.setItem(LTL_FILTER_STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ignore storage errors
    }
  }, []);

  const getTransactionId = useCallback((row) => {
    const value = row?.transaction_id || row?.sonumber || row?.so_number || row?.name || row?.internal_id || '';
    return String(value).trim();
  }, []);

  const extractSsccCodesFromRow = useCallback((row) => {
    if (!row || typeof row !== 'object') return [];

    const key = Object.keys(row).find((k) => {
      const normalized = k.toLowerCase();
      return normalized === 'sscc_codes' || normalized === 'sscc_code' || normalized === 'sscc';
    });

    if (!key || row[key] === null || row[key] === undefined) return [];

    const raw = String(row[key]);
    const splitCodes = raw
      .split(/[;,\n]/)
      .map((item) => item.trim())
      .filter(Boolean);

    return Array.from(new Set(splitCodes));
  }, []);

  const getRowSelectionKey = useCallback((row) => {
    const tx = getTransactionId(row);
    const lineId = String(row?.line_id || row?.internal_id || row?.id || '');
    const item = String(row?.item || row?.name || '');
    const sscc = String(row?.sscc_codes || row?.sscc_code || '');
    const location = String(row?.location || row?.location_id || '');
    const fingerprint = JSON.stringify({
      line_id: row?.line_id ?? '',
      item: row?.item ?? row?.name ?? '',
      sscc_codes: row?.sscc_codes ?? row?.sscc_code ?? '',
      location: row?.location ?? row?.location_id ?? '',
      picked_time: row?.picked_time ?? '',
      picker_name: row?.picker_name ?? '',
      bin_number: row?.bin_number ?? '',
      moment_time: row?.moment_time ?? '',
    });
    return [tx, lineId, item, sscc, location, fingerprint].filter(Boolean).join('|');
  }, [getTransactionId]);

  const updateSelection = useCallback((setter, rowKey, isSelected) => {
    setter((prev) => {
      const next = new Set(prev);
      if (isSelected) {
        next.add(rowKey);
      } else {
        next.delete(rowKey);
      }
      return next;
    });
  }, []);

  const getSelectableRowKeys = useCallback((rows) => {
    if (!Array.isArray(rows)) return [];
    return rows.reduce((acc, row) => {
      if (extractSsccCodesFromRow(row).length > 0) {
        acc.push(getRowSelectionKey(row));
      }
      return acc;
    }, []);
  }, [extractSsccCodesFromRow, getRowSelectionKey]);

  const toggleSelectAllRows = useCallback((setter, rows, isSelected) => {
    setter(isSelected ? new Set(getSelectableRowKeys(rows)) : new Set());
  }, [getSelectableRowKeys]);

  const getSelectedSsccCodes = useCallback((rows, selectedRows) => {
    if (!Array.isArray(rows) || !selectedRows || selectedRows.size === 0) return [];
    const ssccCodes = rows.reduce((acc, row) => {
      const rowKey = getRowSelectionKey(row);
      if (selectedRows.has(rowKey)) {
        acc.push(...extractSsccCodesFromRow(row));
      }
      return acc;
    }, []);
    return Array.from(new Set(ssccCodes));
  }, [extractSsccCodesFromRow, getRowSelectionKey]);

  const salesOrdersQuery = useQuery({
    queryKey: ['sales-orders', shipDateFrom, shipDateTo, transactionId, poNumber],
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    queryFn: async () => {
      try {
        const params = {};
        if (shipDateFrom) params.ship_date_from = shipDateFrom;
        if (shipDateTo) params.ship_date_to = shipDateTo;
        if (transactionId) params.transaction_id = transactionId;
        if (poNumber) params.po_number = poNumber;
        const response = await api.get(endpoints.netsuiteSalesOrderScreen, { params });
        const responseData = response.data?.data || response.data;
        return Array.isArray(responseData) ? responseData : responseData ? [responseData] : [];
      } catch (err) {
        console.error('Error fetching netsuite sales orders:', err);
        throw new Error(
          err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to load sales orders'
        );
      }
    },
  });

  const data = salesOrdersQuery.data || [];
  const isLoading = salesOrdersQuery.isLoading;
  const error = salesOrdersQuery.error?.message || null;
  const { refetch } = salesOrdersQuery;

  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  const openSummaryDialogFromApi = useCallback(async (row, openMode = 'outer') => {
    if (!row) return;
    const transactionId = getTransactionId(row);
    if (!transactionId) return;
    setSelectedTransactionId(transactionId);
    setLineLevelError(null);
    setLineLevelAllowNested(openMode === 'inner');
    setSelectedLineLevelRows(new Set());
    setDetailedData([]);
    setSelectedDetailedRows(new Set());
    setLineLevelLoading(true);
    setLineLevelDialogOpen(true);
    setLineLevelData([]);

    try {
      const response = await api.get(endpoints.netsuiteSoLineLevel, {
        params: { transaction_id: transactionId },
      });
      const responseData = response.data?.data || response.data;
      const nextLineLevelData = Array.isArray(responseData)
        ? responseData
        : responseData
          ? [responseData]
          : [];
      if (openMode === 'inner') {
        const innerRows = nextLineLevelData.flatMap((lineRow) => (Array.isArray(lineRow?.data) ? lineRow.data : []));
        setLineLevelData(innerRows);
        if (innerRows.length === 0) {
          setLineLevelError('No inner data available.');
        }
      } else {
        setLineLevelData(nextLineLevelData);
      }
      if (openMode === 'outer' && nextLineLevelData.length === 0) {
        setLineLevelError('No line-level data available.');
      }
    } catch (err) {
      setLineLevelError(
        err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to load line-level data'
      );
      setLineLevelData([]);
    } finally {
      setLineLevelLoading(false);
    }
  }, [getTransactionId]);

  const handleRowClick = useCallback((row) => {
    openSummaryDialogFromApi(row, 'inner');
  }, [openSummaryDialogFromApi]);

  const handleLineLevelRowClick = useCallback((row) => {
    if (!row || !row.data || !Array.isArray(row.data)) {
      return;
    }

    setDetailedLoading(true);
    setDetailedData(row.data);
    setDetailedLoading(false);
  }, []);

  const handleApplyFilters = useCallback(() => {
    const nextShipDateFrom = shipDateFromDraft;
    const nextShipDateTo = shipDateToDraft;
    const nextTransactionId = transactionIdDraft.trim();
    const nextPoNumber = poNumberDraft.trim();

    setShipDateFrom(nextShipDateFrom);
    setShipDateTo(nextShipDateTo);
    setTransactionId(nextTransactionId);
    setPoNumber(nextPoNumber);

    const filterStateToSave = {
      timestamp: Date.now(),
      shipDateFrom: nextShipDateFrom,
      shipDateTo: nextShipDateTo,
      transactionId: nextTransactionId,
      poNumber: nextPoNumber,
    };

    try {
      sessionStorage.setItem(SESSION_FILTER_KEY, JSON.stringify(filterStateToSave));
    } catch {
      // ignore storage error
    }

    localStorage.setItem('ltl-ship-date-from', nextShipDateFrom || '');
    localStorage.setItem('ltl-ship-date-to', nextShipDateTo || '');
    localStorage.setItem('ltl-transaction-id', nextTransactionId || '');
    localStorage.setItem('ltl-po-number', nextPoNumber || '');
  }, [shipDateFromDraft, shipDateToDraft, transactionIdDraft, poNumberDraft]);

  const mapJsonToZpl = (raw, fallbackSsccCode = '') => {
    const payload = raw?.label_data ? raw.label_data : raw || {};
    const from = payload.shipping_from || {};
    const to = payload.shipping_to || {};

    const customerName = String(payload.customer_name || payload.customer || '').trim();
    console.log('[LTL ZPL] Template label customer_name:', customerName);
    const isAmazonCustomer = customerName.toUpperCase() === 'AMAZON-1YZ7N';

    const nameFrom = String(from.name1 || from.name || from.company || '').trim();
    const secondaryFrom = String(from.name2 || '').trim();
    const addressFrom = String(from.address || '');
    const cityFrom = String(from.city || '');
    const stateFrom = String(from.state || '');
    const zipFrom = String(from.zip || '');

    const companyTo = String(to.company || '');
    const addressTo = String(to.address || '');
    const cityTo = String(to.city || '');
    const stateTo = String(to.state || '');
    const zipTo = String(to.zip || '');

    const company = companyTo.split(',')[0].trim();
    const adress = companyTo.includes(',')
      ? companyTo.split(',').slice(1).join(',').trim()
      : '';

    let adress1 = '';
    let adress2 = '';
    if (addressTo.includes('&')) {
      const parts = addressTo.split('&');
      adress1 = parts[0].trim();
      if (parts.length > 1) {
        adress2 = parts[1].trim();
      }
    } else {
      adress1 = addressTo;
    }

    const city = cityTo;
    let poNumber = String(payload.po_number || '');
    if (poNumber.endsWith('-SC')) {
      poNumber = poNumber.substring(0, poNumber.length - 3);
    } else if (poNumber.endsWith('-NJ')) {
      poNumber = poNumber.substring(0, poNumber.length - 3);
    }

    const model = String(payload.model || '');
    const upc = String(payload.upc || '');
    const cartonInfo = String(payload.carton_info || '');
    const bolNumberDisplay = String(payload.bol_number || '').replaceAll('-', '');
    const ssccCode = String(payload.sscc_code || raw?.sscc_code || fallbackSsccCode || '');
    const ssccDisplay = String(payload.sscc_display || ssccCode);
    const zipCode = String(payload.zip_code || '');
    const zipDisplay = String(payload.zip_display || zipCode);
    const proNumber = String(payload.pro_number || '');
    const carrierName = String(payload.carrier_name || payload.carrier || '').trim();
    const qtyDisplay = '1';

    const hasAdress2 = adress2.trim().length > 0;
    const addressBlock = hasAdress2
      ? `^FO420,165^FD${adress1}^FS\n^FO420,195^FD${adress2}^FS\n^FO420,225^FD${city}, ${stateTo} ${zipTo}^FS`
      : `^FO420,165^FD${adress1}^FS\n^FO420,195^FD${city}, ${stateTo} ${zipTo}^FS`;

    const carrierLabel = isAmazonCustomer ? carrierName : 'CARRIER';
    const isLongCarrier = carrierLabel.trim().length > 22;
    const carrierZpl = isLongCarrier
      ? `^CF0,28\n^FO420,315^FB280,2,4,L,0^FD${carrierLabel}^FS`
      : `^CF0,28\n^FO420,315^FD${carrierLabel}^FS`;
    const proBolFontCmd = isLongCarrier ? '^CF0,22' : '^CF0,24';
    const proY = isLongCarrier ? '385' : '360';
    const bolY = isLongCarrier ? '415' : '390';

    const bolLine = isAmazonCustomer
      ? `^FO420,${bolY}^FDBOL#: ${bolNumberDisplay}^FS`
      : `^FO420,${bolY}^FDBOL#:^FS`;

    const upcLine = isAmazonCustomer
      ? `^FO20,635^FDUPC: ${upc} / SINGLE ASIN^FS`
      : `^FO20,635^FDUPC: ${upc}^FS`;



    const template = `^XA

^PW772

^LL1178

^CI28

^CF0,38

// --------------------------- HEADER: SHIP FROM / SHIP TO ---------------------------
^FO20,30^FDSHIP FROM:^FS
^FO420,30^FDSHIP TO:^FS

^CF0,28
^FO20,100^FD$nameFrom^FS
^FO20,135^FD$secondaryFrom^FS
^FO20,170^FD$addressFrom^FS
^FO20,205^FD$cityFrom, $stateFrom, $zipFrom^FS

^FO420,95^FD$company^FS
^FO420,130^FD$adress^FS
$addressBlock

^FO390,20^GB2,440,7^FS
^FO15,265^GB740,7,7^FS

// --------------------------- ROUTING BARCODE SECTION ---------------------------
^BY2,2,95
^FO55,315^BCN,95,N,N,N^FD$zipCode^FS
^CF0,26
^FO30,420^FB320,1,0,C,0^FD$zipDisplay^FS

// --------------------------- FREIGHT DETAILS ---------------------------
$carrierZpl
$proBolFontCmd
^FO420,$proY^FDPro#: $proNumber^FS
$bolLine
^FO15,460^GB740,7,7^FS

^CF0,28

^FO20,490^FDPO# $poNumber^FS

^BY2,2,85

^FO420,510^BCN,85,N,N,N^FD$poNumber^FS

^CF0,28

$upcLine
^FO20,675^FDQTY: $qtyDisplay^FS

^CF0,28

^FO20,715^FDCARTON:^FS

^FO140,715^FD$cartonInfo^FS

^CF0,28

^FO20,755^FDMODEL: $model^FS

^FO15,795^GB740,7,7^FS

^CF0,30

^FO8,825^FB772,1,0,C,0^FDSSCC - 18^FS

^BY2.5,3,200

^FO2.5,885^BCN,200,N,N,N^FD$ssccCode^FS

^CF0,38

^FO0,1120^FB772,1,0,C,0^FD$ssccDisplay^FS

^XZ`;

    return template
      .replaceAll('$nameFrom', nameFrom)
      .replaceAll('$secondaryFrom', secondaryFrom)
      .replaceAll('$addressFrom', addressFrom)
      .replaceAll('$cityFrom', cityFrom)
      .replaceAll('$stateFrom', stateFrom)
      .replaceAll('$zipFrom', zipFrom)
      .replaceAll('$company', company)
      .replaceAll('$adress', adress)
      .replaceAll('$addressBlock', addressBlock)
      .replaceAll('$zipCode', zipCode)
      .replaceAll('$zipDisplay', zipDisplay)
      .replaceAll('$carrierZpl', carrierZpl)
      .replaceAll('$proBolFontCmd', proBolFontCmd)
      .replaceAll('$proY', String(proY))
      .replaceAll('$bolY', String(bolY))
      .replaceAll('$proNumber', proNumber)
      .replaceAll('$bolLine', bolLine)
      .replaceAll('$upcLine', upcLine)
      .replaceAll('$poNumber', poNumber)
      .replaceAll('$qtyDisplay', qtyDisplay)
      .replaceAll('$cartonInfo', cartonInfo)
      .replaceAll('$model', model)
      .replaceAll('$ssccCode', ssccCode)
      .replaceAll('$ssccDisplay', ssccDisplay);
  };

  const convertZplToImage = async (zpl) => {
    const response = await fetch('https://api.labelary.com/v1/printers/8dpmm/labels/4x6/0/', {
      method: 'POST',
      headers: {
        Accept: 'image/png',
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: zpl
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to convert ZPL to image: ${errorText}`);
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  };

  const generatePdfWithRetry = async (zpl, maxRetries = 3, delay = 500) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch('https://api.labelary.com/v1/printers/8dpmm/labels/4x6/0/', {
          method: 'POST',
          headers: {
            Accept: 'application/pdf',
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: zpl
        });

        if (!response.ok) {
          if (attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, delay * attempt));
            continue;
          }
          const errorText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        return await response.arrayBuffer();
      } catch (error) {
        if (attempt === maxRetries) {
          throw error;
        }
        await new Promise((resolve) => setTimeout(resolve, delay * attempt));
      }
    }
    throw new Error('Failed to generate PDF');
  };

  const downloadCombinedPdf = async (zplArray, filename = 'labels.pdf') => {
    try {
      if (!zplArray || zplArray.length === 0) {
        setLabelError('No labels to download');
        return;
      }

      setPdfDownloading(true);
      setLabelError(null);

      const mergedPdf = await PDFDocument.create();
      let successCount = 0;
      let failedCount = 0;

      for (let i = 0; i < zplArray.length; i += 1) {
        try {
          const pdfBytes = await generatePdfWithRetry(zplArray[i]);
          const pdfDoc = await PDFDocument.load(pdfBytes);
          const pages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
          pages.forEach((page) => mergedPdf.addPage(page));
          successCount += 1;
          if (i < zplArray.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
        } catch {
          failedCount += 1;
        }
      }

      if (mergedPdf.getPageCount() === 0) {
        throw new Error('No labels could be generated');
      }

      if (failedCount > 0) {
        setLabelError(`Generated ${successCount} of ${zplArray.length} labels. ${failedCount} label(s) failed.`);
      }

      const mergedPdfBytes = await mergedPdf.save();
      const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setLabelError(err?.message || 'Failed to generate combined PDF');
    } finally {
      setPdfDownloading(false);
    }
  };

  const buildLabelPreviewItem = useCallback(async (ssccCode) => {
    const response = await api.get(`/item-reprint-label?sscc_code=${encodeURIComponent(ssccCode)}`);
    const labelData = response.data;
    const zpl = mapJsonToZpl(labelData, ssccCode);
    const imageUrl = await convertZplToImage(zpl);
    return { ssccCode, imageUrl, zpl };
  }, []);

  const clearLabelImages = useCallback(() => {
    setLabelImages((prev) => {
      prev.forEach((item) => {
        if (item?.imageUrl) URL.revokeObjectURL(item.imageUrl);
      });
      return [];
    });
  }, []);

  const handleGenerateMultipleLabels = useCallback(async (ssccCodes) => {
    const uniqueCodes = Array.from(new Set((ssccCodes || []).filter(Boolean)));
    if (uniqueCodes.length === 0) {
      setLabelError('Please select at least one row with SSCC code.');
      return;
    }

    setLabelLoading(true);
    setLabelError(null);
    clearLabelImages();
    setAllZplLabels([]);
    setLabelImageOpen(true);

    const previewItems = [];
    const zplLabels = [];
    const warnings = [];

    try {
      for (const ssccCode of uniqueCodes) {
        try {
          const item = await buildLabelPreviewItem(ssccCode);
          previewItems.push(item);
          zplLabels.push(item.zpl);
        } catch (err) {
          let errorMsg = `${ssccCode}: `;
          if (err?.response?.status === 404) {
            errorMsg += 'Item is not picked';
          } else {
            errorMsg += err?.response?.data?.message || err?.message || 'Failed to load label';
          }
          warnings.push(errorMsg);
        }
      }

      setLabelImages(previewItems);
      setAllZplLabels(zplLabels);

      if (warnings.length > 0) {
        setLabelError(warnings.join('\n'));
      } else if (previewItems.length === 0) {
        setLabelError('No labels could be generated');
      }
    } finally {
      setLabelLoading(false);
    }
  }, [buildLabelPreviewItem, clearLabelImages]);

  const isItemNotPicked = useCallback((row) => {
    if (!row || typeof row !== 'object') return false;
    const pickedValue = row?.picked || row?.is_picked || row?.ispicked;
    if (pickedValue === null || pickedValue === undefined || pickedValue === '') return false;
    const str = String(pickedValue).trim().toLowerCase();
    return str === '0' || str === 'false' || str === 'not picked';
  }, []);

  const handleGenerateLabel = useCallback((row) => {
    const ssccCodes = extractSsccCodesFromRow(row);
    if (ssccCodes.length === 0) {
      setLabelError('No SSCC code found for this row');
      setLabelImageOpen(true);
      return;
    }
    handleGenerateMultipleLabels([ssccCodes[0]]);
  }, [extractSsccCodesFromRow, handleGenerateMultipleLabels]);

  const handleGenerateSelectedLabels = useCallback((rows, selectedRows) => {
    const ssccCodes = getSelectedSsccCodes(rows, selectedRows);
    if (ssccCodes.length === 0) {
      setLabelError('Please select rows with SSCC codes');
      setLabelImageOpen(true);
      return;
    }
    return handleGenerateMultipleLabels(ssccCodes);
  }, [getSelectedSsccCodes, handleGenerateMultipleLabels]);

  const lineLevelSelectableRowKeys = useMemo(() => getSelectableRowKeys(lineLevelData), [lineLevelData, getSelectableRowKeys]);
  const detailedSelectableRowKeys = useMemo(() => getSelectableRowKeys(detailedData), [detailedData, getSelectableRowKeys]);

  const createSelectionColumn = useCallback((selectedRows, setSelectedRows) => ({
    accessorKey: '__select_labels',
    header: '\u200B',
    minSize: 60,
    size: 70,
    forceFirst: true,
    cell: (info) => {
      const row = info.row?.original || info.getValue();
      const ssccCodes = extractSsccCodesFromRow(row);
      if (ssccCodes.length === 0) return '-';

      const rowKey = getRowSelectionKey(row);
      return (
        <Box
          data-checkbox-cell="true"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}
        >
          <Checkbox
            checked={selectedRows.has(rowKey)}
            onChange={(e) => {
              e.stopPropagation();
              updateSelection(setSelectedRows, rowKey, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
        </Box>
      );
    }
  }), [extractSsccCodesFromRow, getRowSelectionKey, updateSelection]);

  const buildLineLevelColumns = useCallback((selectedRows, setSelectedRows) => {
    const rows = Array.isArray(lineLevelData) ? lineLevelData : [];
    if (rows.length === 0) {
      return [createSelectionColumn(selectedRows, setSelectedRows)];
    }

    const orderedKeys = [
      'line_id',
      'item',
      'quantity',
      'location',
      'ready_for_picking',
      'picked',
      'ship_confirmed',
      'tracking_number',
      'sscc_codes',
      'packing_slip_id',
      'bin_number',
      'picked_time',
      'picker_name',
      'ship_confirm_time',
      'movement',
      'moment_time'
    ];

    const keys = Object.keys(rows[0] || {});
    const dynamicKeys = [
      ...orderedKeys.filter((k) => keys.includes(k)),
      ...keys.filter((k) => !orderedKeys.includes(k) && k !== 'data')
    ];

    const selectionColumn = createSelectionColumn(selectedRows, setSelectedRows);
    const generateLabelColumn = {
      accessorKey: '__generate_label',
      header: 'Generate Label',
      minSize: 120,
      size: 140,
      cell: (info) => {
        const row = info.row?.original || info.getValue();
        const hasSscc = extractSsccCodesFromRow(row).length > 0;
        if (!hasSscc) return '-';

        return (
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleGenerateLabel(row)}
            disabled={labelLoading}
          >
            Generate Label
          </Button>
        );
      }
    };

    const otherColumns = dynamicKeys.map((key) => ({
      accessorKey: key,
      header: key
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: (info) => {
        const value = info.getValue();
        if (value === null || value === undefined || value === '') return '-';
        const str = String(value);
        if ((key.includes('picked') || key.includes('ready_for_pick') || key.includes('ready_for_picking')) && str.includes('/')) {
          return str.split('/')[0].trim();
        }
        return str;
      }
    }));

    return [selectionColumn, generateLabelColumn, ...otherColumns];
  }, [createSelectionColumn, extractSsccCodesFromRow, handleGenerateLabel, labelLoading, lineLevelData]);

  const lineLevelColumns = useMemo(
    () => buildLineLevelColumns(selectedLineLevelRows, setSelectedLineLevelRows),
    [buildLineLevelColumns, selectedLineLevelRows]
  );

  const summaryLineLevelColumns = useMemo(
    () => lineLevelColumns.filter((column) => column.accessorKey !== '__select_labels' && column.accessorKey !== '__generate_label'),
    [lineLevelColumns]
  );

  const detailedColumns = useMemo(() => {
    if (!detailedData || detailedData.length === 0) return [];

    const orderedKeys = [
      'line_id',
      'item',
      'po',
      'tracking_number',
      'sscc_codes',
      'packing_slip_id',
      'bin_number',
      'picked_time',
      'picker_name',
      'ship_confirm_time',
      'movement',
      'moment_time'
    ];

    const keys = Object.keys(detailedData[0] || {});
    const dynamicKeys = [
      ...orderedKeys.filter((k) => keys.includes(k)),
      ...keys.filter((k) => !orderedKeys.includes(k) && k !== 'data')
    ];

    const selectionColumn = createSelectionColumn(selectedDetailedRows, setSelectedDetailedRows);
    const generateLabelColumn = {
      accessorKey: '__generate_label',
      header: 'Generate Label',
      minSize: 120,
      size: 140,
      cell: (info) => {
        const row = info.row?.original || info.getValue();
        const hasSscc = extractSsccCodesFromRow(row).length > 0;
        if (!hasSscc) return '-';

        return (
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleGenerateLabel(row)}
            disabled={labelLoading}
          >
            Generate Label
          </Button>
        );
      }
    };

    const otherColumns = dynamicKeys.map((key) => ({
      accessorKey: key,
      header: key
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: (info) => {
        const value = info.getValue();
        if (value === null || value === undefined || value === '') return '-';
        const str = String(value);
        if ((key.includes('picked') || key.includes('ready_for_pick') || key.includes('ready_for_picking')) && str.includes('/')) {
          return str.split('/')[0].trim();
        }
        return str;
      }
    }));

    return [selectionColumn, generateLabelColumn, ...otherColumns];
  }, [createSelectionColumn, detailedData, extractSsccCodesFromRow, handleGenerateLabel, labelLoading, selectedDetailedRows]);

  const columns = useMemo(() => {
    if (!data || data.length === 0) return [];

    const orderedKeys = [
      'transaction_id',
      'name',
      'status',
      'ponumber',
      'location',
      'ship_from',
      'reference_1',
      'customer_order_number',
      'scheduled_pickup_date',
      'scheduled_ship_date',
      'carrier_name',
      'picked',
      'date_created'
    ];

    const keys = Object.keys(data[0] || {});
    const dynamicKeys = [
      ...orderedKeys.filter((k) => keys.includes(k)),
      ...keys.filter((k) => !orderedKeys.includes(k) && k !== 'data')
    ];

    const viewButtonColumn = {
      accessorKey: 'view_details',
      header: 'View Details',
      forceFirst: true,
      minSize: 90,
      size: 110,
      cell: (info) => {
        const row = info.row?.original || info.getValue();
        return (
          <Button
            size="small"
            variant="contained"
            onClick={(event) => {
              if (event && event.stopPropagation) event.stopPropagation();
              openSummaryDialogFromApi(row);
            }}
          >
            View Details
          </Button>
        );
      }
    };

    const generatedColumns = dynamicKeys.map((key) => ({
      accessorKey: key,
      header: key
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
      minSize: COLUMN_MIN_SIZE,
      size: COLUMN_SIZE,
      cell: (info) => {
        const value = info.getValue();
        if (value === null || value === undefined || value === '') return '-';
        const str = String(value);
        if ((key.includes('picked') || key.includes('ready_for_pick') || key.includes('ready_for_picking')) && str.includes('/')) {
          return str.split('/')[0].trim();
        }
        return str;
      }
    }));

    return [viewButtonColumn, ...generatedColumns];
  }, [data, openSummaryDialogFromApi]);

  const getRowId = (row, index) => {
    const tx = String(getTransactionId(row));
    const line = String(row?.line_id || row?.internal_id || row?.id || '');
    const item = String(row?.item || row?.name || '');
    return ['row', tx, line, item, index !== undefined ? String(index) : ''].filter(Boolean).join('_');
  };

  const exportValueTransform = (val, _row, key) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'boolean') return val ? 'TRUE' : 'FALSE';

    let processedVal = val;
    const str = String(val);
    const shouldFormat = key && (key.includes('picked') || key.includes('ready_for_pick') || key.includes('ready_for_picking'));
    if (shouldFormat && str.includes('/')) {
      processedVal = str.split('/')[0].trim();
    }

    if (Array.isArray(processedVal)) return processedVal.map((item) => (typeof item === 'object' ? JSON.stringify(item) : String(item))).join('; ');
    if (typeof processedVal === 'object') return JSON.stringify(processedVal);
    return String(processedVal);
  };

  const salesOrdersLineLevelDialogColumns = useMemo(
    () => (lineLevelAllowNested ? lineLevelColumns : summaryLineLevelColumns),
    [lineLevelAllowNested, lineLevelColumns, summaryLineLevelColumns]
  );

  const salesOrdersMainGrid = useDataGridProTable({
    data,
    loading: isLoading,
    error: error || null,
    title: 'LTL Sales Orders',
    columns,
    onDataRefresh: handleRefetch,
    getRowId: (row, index) => getRowId(row, index),
    exportValueTransform,
    initialColumnVisibility: columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: handleRowClick,
    muiTableContainerProps: { sx: {} },
    // Persist and restore column filters
    initialFilterItems: savedFilterItems,
    onColumnFiltersChange: handleColumnFiltersChange,
    actionButtons: [
      <Box key="filters" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Ship Date From"
          type="date"
          value={shipDateFromDraft}
          onChange={(e) => setShipDateFromDraft(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={getFutureDateInputProps()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <CalendarMonthIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{
            minWidth: 170,
            '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
            '& .MuiInputLabel-root': { fontSize: '0.8125rem' }
          }}
        />
        <TextField
          size="small"
          label="Ship Date To"
          type="date"
          value={shipDateToDraft}
          onChange={(e) => setShipDateToDraft(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={getFutureDateInputProps()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <CalendarMonthIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{
            minWidth: 170,
            '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
            '& .MuiInputLabel-root': { fontSize: '0.8125rem' }
          }}
        />
        <TextField
          size="small"
          label="SO ID"
          value={transactionIdDraft}
          onChange={(e) => setTransactionIdDraft(e.target.value)}
          sx={{
            minWidth: 180,
            '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
            '& .MuiInputLabel-root': { fontSize: '0.8125rem' }
          }}
        />
        <TextField
          size="small"
          label="PO Number"
          value={poNumberDraft}
          onChange={(e) => setPoNumberDraft(e.target.value)}
          sx={{
            minWidth: 170,
            '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
            '& .MuiInputLabel-root': { fontSize: '0.8125rem' }
          }}
        />
        <Button
          variant="outlined"
          onClick={handleApplyFilters}
          sx={{
            fontSize: '0.8125rem',
            height: '32px',
            textTransform: 'none',
            px: 2
          }}
        >
          Apply Filters
        </Button>
      </Box>,
    ],
  });

  const salesOrdersLineLevelGrid = useDataGridProTable({
    data: lineLevelData || [],
    columns: salesOrdersLineLevelDialogColumns,
    loading: false,
    title: 'sales-orders-line-level',
    exportValueTransform,
    initialColumnVisibility: lineLevelColumnVisibility,
    onColumnVisibilityChange: setLineLevelColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: lineLevelAllowNested ? handleLineLevelRowClick : undefined,
    muiTableContainerProps: { sx: {} },
    disablePaper: true,
    error: null,
  });

  const salesOrdersDetailedGrid = useDataGridProTable({
    data: detailedData || [],
    columns: detailedColumns,
    loading: false,
    title: 'sales-orders-detailed',
    exportValueTransform,
    initialColumnVisibility: detailedColumnVisibility,
    onColumnVisibilityChange: setDetailedColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    muiTableContainerProps: { sx: {} },
    disablePaper: true,
    error: null,
  });

  // ── Active-filter chips derived from the controlled filterModel ─────────
  const activeFilterItems = salesOrdersMainGrid.filterModel?.items ?? [];

  // Map a field name to a human-readable column header
  const getColumnHeader = useCallback(
    (field) => {
      if (!columns || columns.length === 0) return field;
      const col = columns.find((c) => c.accessorKey === field);
      return col?.header ?? field;
    },
    [columns]
  );

  // Remove a single filter item by its id
  const handleRemoveFilterItem = useCallback(
    (itemId) => {
      const nextItems = activeFilterItems.filter((f) => f.id !== itemId);
      salesOrdersMainGrid.setFilterModel((prev) => ({ ...prev, items: nextItems }));
      handleColumnFiltersChange(nextItems);
    },
    [activeFilterItems, salesOrdersMainGrid, handleColumnFiltersChange]
  );

  // Clear all column filter items
  const handleClearAllFilters = useCallback(() => {
    salesOrdersMainGrid.setFilterModel((prev) => ({ ...prev, items: [] }));
    handleColumnFiltersChange([]);
  }, [salesOrdersMainGrid, handleColumnFiltersChange]);

  return (
    <DashboardLayout>
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <MuiXDataGridSurface
          hook={salesOrdersMainGrid}
          DataGridPro={DataGridPro}
          aboveTable={
            activeFilterItems.length > 0 && (
              <Box
                sx={{
                  px: 1,
                  pt: 0.75,
                  pb: 0.25,
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 0.75,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mr: 0.5, whiteSpace: 'nowrap' }}>
                  Active filters:
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {activeFilterItems.map((item, idx) => {
                    const header = getColumnHeader(item.field);
                    const opLabel = item.operator ? ` ${item.operator} ` : ': ';
                    const hasValue = item.value !== undefined && item.value !== null && item.value !== '';
                    const label = hasValue ? `${header}${opLabel}${item.value}` : `${header} (${item.operator || 'active'})`;
                    return (
                      <Chip
                        key={item.id ?? `${item.field}-${item.operator}-${item.value}-${idx}`}
                        label={label}
                        size="small"
                        onDelete={() => handleRemoveFilterItem(item.id)}
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem', height: 24 }}
                      />
                    );
                  })}
                </Stack>
                {activeFilterItems.length > 1 && (
                  <Button
                    size="small"
                    variant="text"
                    color="error"
                    startIcon={<FilterListOffIcon fontSize="small" />}
                    onClick={handleClearAllFilters}
                    sx={{ ml: 0.5, fontSize: '0.75rem', textTransform: 'none', height: 24, py: 0, px: 1 }}
                  >
                    Clear All
                  </Button>
                )}
              </Box>
            )
          }
        />
      </Box>

      <Dialog
        open={lineLevelDialogOpen}
        onClose={() => { setLineLevelDialogOpen(false); setLineLevelAllowNested(true); }}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: NEAR_FULLSCREEN_DIALOG_PAPER_SX }}
      >
        <DialogTitle sx={{ p: 2, pb: 1.5, pr: 10, position: 'relative' }}>
          <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.6 }}>
            Summary Data - {selectedTransactionId || 'N/A'}
          </Typography>
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => { setLineLevelDialogOpen(false); setLineLevelAllowNested(true); }}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {lineLevelLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : lineLevelError ? (
            <Typography color="error" sx={{ p: 2 }}>
              {lineLevelError}
            </Typography>
          ) : lineLevelData && lineLevelData.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              {lineLevelAllowNested && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, p: 2, pb: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      checked={lineLevelSelectableRowKeys.length > 0 && selectedLineLevelRows.size === lineLevelSelectableRowKeys.length}
                      indeterminate={selectedLineLevelRows.size > 0 && selectedLineLevelRows.size < lineLevelSelectableRowKeys.length}
                      onChange={(e) => toggleSelectAllRows(setSelectedLineLevelRows, lineLevelData, e.target.checked)}
                      disabled={lineLevelSelectableRowKeys.length === 0}
                      size="small"
                    />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {selectedLineLevelRows.size > 0 ? `${selectedLineLevelRows.size} selected` : 'Select rows to generate labels'}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={labelLoading ? <CircularProgress size={16} /> : <PrintIcon />}
                    onClick={() => handleGenerateSelectedLabels(lineLevelData, selectedLineLevelRows)}
                    disabled={labelLoading || selectedLineLevelRows.size === 0}
                  >
                    Generate Labels ({selectedLineLevelRows.size})
                  </Button>
                </Box>
              )}
              <MuiXDataGridSurface hook={salesOrdersLineLevelGrid} DataGridPro={DataGridPro} />
              {detailedLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '160px', pt: 1 }}>
                  <CircularProgress />
                </Box>
              ) : detailedData && detailedData.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', pt: 1 }}>
                  <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Inner Data
                    </Typography>
                  </Box>
                  <MuiXDataGridSurface hook={salesOrdersDetailedGrid} DataGridPro={DataGridPro} />
                </Box>
              ) : null}
            </Box>
          ) : (
            <Typography sx={{ p: 2 }}>No line-level data available.</Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={detailedDialogOpen}
        onClose={() => setDetailedDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: NEAR_FULLSCREEN_DIALOG_PAPER_SX }}
      >
        <DialogTitle sx={{ p: 2, pb: 1.5, pr: 10, position: 'relative' }}>
          <Typography component="span" sx={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.6 }}>Detailed Data</Typography>
          <IconButton
            aria-label="close"
            size="small"
            onClick={() => setDetailedDialogOpen(false)}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {detailedLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : detailedData && detailedData.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, p: 2, pb: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    checked={detailedSelectableRowKeys.length > 0 && selectedDetailedRows.size === detailedSelectableRowKeys.length}
                    indeterminate={selectedDetailedRows.size > 0 && selectedDetailedRows.size < detailedSelectableRowKeys.length}
                    onChange={(e) => toggleSelectAllRows(setSelectedDetailedRows, detailedData, e.target.checked)}
                    disabled={detailedSelectableRowKeys.length === 0}
                    size="small"
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {selectedDetailedRows.size > 0 ? `${selectedDetailedRows.size} selected` : 'Select rows to generate labels'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={labelLoading ? <CircularProgress size={16} /> : <PrintIcon />}
                  onClick={() => handleGenerateSelectedLabels(detailedData, selectedDetailedRows)}
                  disabled={labelLoading || selectedDetailedRows.size === 0}
                >
                  Generate Labels ({selectedDetailedRows.size})
                </Button>
              </Box>
              <MuiXDataGridSurface hook={salesOrdersDetailedGrid} DataGridPro={DataGridPro} />
            </Box>
          ) : (
            <Typography sx={{ p: 2 }}>No detailed data available.</Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={labelImageOpen}
        onClose={() => {
          setLabelImageOpen(false);
          clearLabelImages();
          setAllZplLabels([]);
          setPdfDownloading(false);
        }}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '95vw',
            maxWidth: '1400px',
            maxHeight: '95vh',
            height: '95vh'
          }
        }}
      >
        <DialogTitle>
          Label Preview{labelImages.length > 1 ? ` (${labelImages.length} labels)` : ''}
          <IconButton
            aria-label="download pdf"
            onClick={() => {
              if (allZplLabels.length > 0) {
                downloadCombinedPdf(allZplLabels, `labels_${new Date().getTime()}.pdf`);
              }
            }}
            disabled={allZplLabels.length === 0}
            sx={{ position: 'absolute', right: 56, top: 8 }}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            aria-label="close"
            onClick={() => {
              setLabelImageOpen(false);
              clearLabelImages();
              setAllZplLabels([]);
              setPdfDownloading(false);
            }}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {labelError ? (
            <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{labelError}</Alert>
          ) : null}
          {labelLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading labels...</Typography>
            </Box>
          ) : pdfDownloading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Downloading labels, please wait...</Typography>
            </Box>
          ) : null}

          {labelImages.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                p: 2,
                maxHeight: 'calc(90vh - 200px)',
                overflowY: 'auto'
              }}
            >
              {labelImages.map(({ ssccCode, imageUrl }, index) => (
                <Box
                  key={ssccCode || index}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  {ssccCode && (
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      SSCC: {ssccCode}
                    </Typography>
                  )}
                  <img
                    src={imageUrl}
                    alt={`Label Preview ${index + 1}`}
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default SalesOrders;
